import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Typography, Paper, Button, Tabs, Tab, Grid, Card, CardContent, CardActions,
  Chip, Avatar, Divider, Grow, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, TextField, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Autocomplete, Alert,
  Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import BusinessIcon from '@mui/icons-material/Business';
import PublicIcon from '@mui/icons-material/Public';
import FolderIcon from '@mui/icons-material/Folder';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ForumIcon from '@mui/icons-material/Forum';
import SendIcon from '@mui/icons-material/Send';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import EventNoteIcon from '@mui/icons-material/EventNote';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import FlagIcon from '@mui/icons-material/Flag';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PsychologyIcon from '@mui/icons-material/Psychology';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import API_URL from '../../config';
import Loading from '../../components/Loading';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`quote-tabpanel-${index}`} aria-labelledby={`quote-tab-${index}`} {...other}>
      {value === index && <Box sx={{ py: 3, px: { xs: 1, sm: 2, md: 3 }, overflowX: 'hidden' }}>{children}</Box>}
    </div>
  );
}

// Workflow cycle node (circle) + connecting arrow
function WorkflowNode({ label, active, status, disabled, isResult, onClick }) {
  const color = status === 'yes' ? '#10b981' : status === 'no' ? '#ef4444' : '#cbd5e1';
  const bg = status === 'yes' ? '#ecfdf5' : status === 'no' ? '#fef2f2' : '#f8fafc';
  const borderColor = active ? color : (disabled ? '#e2e8f0' : color);
  const opacity = disabled ? 0.45 : 1;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, opacity }}>
      <Box
        onClick={disabled ? undefined : onClick}
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          border: `3px solid ${borderColor}`,
          bgcolor: bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled ? 'default' : 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': disabled ? {} : { transform: 'scale(1.08)', boxShadow: '0 6px 16px rgba(0,0,0,0.12)' },
        }}
      >
        {status === 'yes' ? (
          <CheckCircleIcon sx={{ color: '#10b981', fontSize: 24 }} />
        ) : status === 'no' ? (
          <CancelIcon sx={{ color: '#ef4444', fontSize: 24 }} />
        ) : (
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: active ? color : '#cbd5e1' }} />
        )}
      </Box>
      <Typography variant="caption" sx={{ fontWeight: 700, color: active ? '#1e293b' : '#94a3b8', textAlign: 'center', maxWidth: 90, lineHeight: 1.1 }}>
        {label}
      </Typography>
      {!isResult && (
        <Typography variant="caption" sx={{ color, lineHeight: 1 }}>
          {status === 'yes' ? 'Yes' : status === 'no' ? 'No' : 'Pending'}
        </Typography>
      )}
    </Box>
  );
}

function WorkflowArrow({ active }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', color: active ? '#3b82f6' : '#cbd5e1', fontWeight: 900, fontSize: 22, px: { xs: 0.5, sm: 1 } }}>
      ➜
    </Box>
  );
}

// Convert an ISO datetime string to a yyyy-mm-dd value for <input type="date" />
function toDateInputValue(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function QuoteDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  // Head approval is restricted to Manager / Admin roles.
  const userRole = localStorage.getItem('user_role') || '';
  const canApproveHead = userRole === 'Admin' || (userRole && userRole.toLowerCase().endsWith('manager'));

  // Followup states
  const [quoteFollowups, setQuoteFollowups] = useState([]);
  const [quoteConversations, setQuoteConversations] = useState([]);
  const [quoteDocuments, setQuoteDocuments] = useState([]);
  const [quoteAssignments, setQuoteAssignments] = useState([]);
  const [quoteMilestones, setQuoteMilestones] = useState([]);

  const [newFollowup, setNewFollowup] = useState('');
  const [newFollowupDesc, setNewFollowupDesc] = useState('');
  const [newFollowupStartDate, setNewFollowupStartDate] = useState('');
  const [newFollowupStartTime, setNewFollowupStartTime] = useState('');
  const [newFollowupEndDate, setNewFollowupEndDate] = useState('');
  const [newFollowupEndTime, setNewFollowupEndTime] = useState('');
  const [newFollowupNextVisitStartDate, setNewFollowupNextVisitStartDate] = useState('');
  const [newFollowupNextVisitStartTime, setNewFollowupNextVisitStartTime] = useState('');
  const [newFollowupNextVisitEndDate, setNewFollowupNextVisitEndDate] = useState('');
  const [newFollowupNextVisitEndTime, setNewFollowupNextVisitEndTime] = useState('');
  const [newFollowupAssignee, setNewFollowupAssignee] = useState('');
  const [openFollowupDialog, setOpenFollowupDialog] = useState(false);
  const [editingFollowupId, setEditingFollowupId] = useState(null);

  const [newConversation, setNewConversation] = useState('');
  const [newAssignment, setNewAssignment] = useState('');
  const [newMilestone, setNewMilestone] = useState('');
  const [newMilestoneDate, setNewMilestoneDate] = useState('');

  const [users, setUsers] = useState([]);
  const [openMilestoneDialog, setOpenMilestoneDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [winPrediction, setWinPrediction] = useState(null);

  // AI Analyze states
  const [aiAnalyzeLoading, setAiAnalyzeLoading] = useState(false);
  const [aiAnalyzeData, setAiAnalyzeData] = useState(null);
  const [aiAnalyzeError, setAiAnalyzeError] = useState(null);

  // SHAP impacts from batch-predict (feature contributions)
  const [shapImpacts, setShapImpacts] = useState(null);

  // Product (line items) states
  const [allProducts, setAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [uomOptions, setUomOptions] = useState([]);
  const [openAddProductDialog, setOpenAddProductDialog] = useState(false);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productForm, setProductForm] = useState({ quantity: '', price_per_unit: '', description: '', uom_id: '', unit: '' });
  const [editingProductId, setEditingProductId] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const [productError, setProductError] = useState('');
  const [deleteProductTarget, setDeleteProductTarget] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(false);

  // Quote status workflow states
  const [statusForm, setStatusForm] = useState({
    quote_status: '',
    quote_closed_status: '',
    reason_if_lost: '',
    submit: '',
    head_approved: '',
  });
  const [savingStatus, setSavingStatus] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [statusSuccess, setStatusSuccess] = useState('');
  const [qrData, setQrData] = useState(null);

  // Quote Dates editing states
  const [datesForm, setDatesForm] = useState({
    quote_requested_date: '',
    quote_submission_date: '',
    quote_due_date: '',
    quote_submission_type: '',
  });
  const [savingDates, setSavingDates] = useState(false);
  const [datesError, setDatesError] = useState('');
  const [datesSuccess, setDatesSuccess] = useState('');

  // Quote revision states
  const [revising, setRevising] = useState(false);
  const [reviseDialogOpen, setReviseDialogOpen] = useState(false);
  const [reviseError, setReviseError] = useState('');

  // Create Project from Won quote states
  const [createProjectDialogOpen, setCreateProjectDialogOpen] = useState(false);
  const [creatingProjectFromQuote, setCreatingProjectFromQuote] = useState(false);
  const [createProjectError, setCreateProjectError] = useState('');
  const [countries, setCountries] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [createProjectData, setCreateProjectData] = useState({
    name: '', quote_ref: '', start_date: '', due_date: '',
    country_id: '', division_id: '', description: '', priority: 'Medium',
    quote_id: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchQuoteDetail();
    fetchQrCode();
    fetchUsers();
    fetchFollowups();
    fetchConversations();
    fetchDocuments();
    fetchAssignments();
    fetchMilestones();
    fetchCountries();
    fetchDivisions();
  }, [id]);

  useEffect(() => {
    if (quote && quote.statecode !== 2 && quote.statecode !== 3) {
      fetchWinPrediction(quote.quoteid);
    } else {
      setWinPrediction(null);
    }
  }, [quote]);

  // Auto-call AI Analyze when tab 7 is selected
  useEffect(() => {
    if (tabValue === 7 && quote && !aiAnalyzeData && !aiAnalyzeLoading) {
      fetchAiAnalyze();
    }
  }, [tabValue, quote]);

  const fetchQuoteDetail = async () => {
    try {
      const res = await axios.get(`${API_URL}/quotes/${id}`, { headers });
      setQuote(res.data);
      setStatusForm({
        quote_status: res.data.quote_status_id || '',
        quote_closed_status: res.data.quote_closed_status_id || '',
        reason_if_lost: res.data.reason_if_lost_id || '',
        submit: res.data.submit_id || '',
        head_approved: res.data.head_approved_id || '',
      });
      setDatesForm({
        quote_requested_date: toDateInputValue(res.data.quote_requested_date),
        quote_submission_date: toDateInputValue(res.data.quote_submission_date),
        quote_due_date: toDateInputValue(res.data.quote_due_date),
        quote_submission_type: res.data.quote_submission_type_id ?? '',
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchQrCode = async () => {
    try {
      const res = await axios.get(`${API_URL}/quotes/${id}/qr`, { headers });
      setQrData(res.data);
    } catch (err) {
      console.error('Error fetching QR code:', err);
      setQrData(null);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/setup/users`, { headers });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API_URL}/setup/countries`, { headers });
      setCountries(response.data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchDivisions = async () => {
    try {
      const response = await axios.get(`${API_URL}/setup/divisions`, { headers });
      setDivisions(response.data || []);
    } catch (error) {
      console.error('Error fetching divisions:', error);
    }
  };

  const fetchFollowups = async () => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${id}/followups`, { headers });
      setQuoteFollowups(response.data);
    } catch (error) { console.error('Error fetching followups:', error); }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${id}/conversations`, { headers });
      setQuoteConversations(response.data);
    } catch (error) { console.error('Error fetching conversations:', error); }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${id}/documents`, { headers });
      setQuoteDocuments(response.data);
    } catch (error) { console.error('Error fetching documents:', error); }
  };

  const fetchAssignments = async () => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${id}/assignments`, { headers });
      setQuoteAssignments(response.data);
    } catch (error) { console.error('Error fetching assignments:', error); }
  };

  const fetchMilestones = async () => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${id}/milestones`, { headers });
      setQuoteMilestones(response.data);
    } catch (error) { console.error('Error fetching milestones:', error); }
  };

  // ───── Product (line items) handlers ─────
  const fetchUoms = async () => {
    try {
      const res = await axios.get(`${API_URL}/quotes/uoms`, { headers });
      setUomOptions(res.data || []);
    } catch (error) {
      console.error('Error fetching uoms:', error);
      setUomOptions([]);
    }
  };

  const fetchAllProducts = async (priceListId) => {
    setLoadingProducts(true);
    try {
      const url = priceListId
        ? `${API_URL}/quotes/products?price_list_id=${priceListId}`
        : `${API_URL}/quotes/products`;
      const res = await axios.get(url, { headers });
      setAllProducts(res.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setAllProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleOpenAddProduct = () => {
    setProductError('');
    setSelectedProduct(null);
    setEditingProductId(null);
    setPendingProducts([]);
    setProductForm({ quantity: '', price_per_unit: '', description: '', uom_id: '', unit: '' });
    setOpenAddProductDialog(true);
    fetchAllProducts(quote?.price_list_id);
    fetchUoms();
  };

  const handleSelectProduct = (event, value) => {
    setSelectedProduct(value);
    if (value) {
      setProductForm((prev) => ({
        ...prev,
        description: value.description || value.name || '',
        price_per_unit: value.price != null ? String(value.price) : '',
        uom_id: value.price_uom_id || value.default_uom_id || '',
        unit: value.price_unit || value.unit || '',
        quantity: prev.quantity || '1',
      }));
    }
  };

  const handleProductFormChange = (field, val) => {
    setProductForm((prev) => ({ ...prev, [field]: val }));
  };

  // Add the currently-selected product (currentItem) to the pending list
  const handleAddCurrentItem = () => {
    if (!selectedProduct || !selectedProduct.product_id) {
      setProductError('Please select a product.');
      return;
    }
    setProductError('');
    const item = {
      key: `${selectedProduct.product_id}-${Date.now()}`,
      product_id: selectedProduct.product_id,
      name: selectedProduct.name || '',
      quantity: productForm.quantity,
      price_per_unit: productForm.price_per_unit,
      description: productForm.description,
      uom_id: productForm.uom_id,
      unit: productForm.unit,
      uom_label: productForm.unit || (uomOptions.find(u => u.value === productForm.uom_id)?.label) || '',
    };
    setPendingProducts((prev) => [...prev, item]);
    // Reset selector for the next item
    setSelectedProduct(null);
    setProductForm({ quantity: '', price_per_unit: '', description: '', uom_id: '', unit: '' });
  };

  const handleRemovePendingItem = (key) => {
    setPendingProducts((prev) => prev.filter((p) => p.key !== key));
  };

  const handleUpdatePendingItem = (key, field, value) => {
    setPendingProducts((prev) => prev.map((p) => {
      if (p.key !== key) return p;
      const updated = { ...p, [field]: value };
      // Keep unit label in sync when uom_id changes
      if (field === 'uom_id') {
        updated.uom_label = uomOptions.find(u => u.value === value)?.label || '';
      }
      return updated;
    }));
  };

  const handleAddProductSubmit = async () => {
    if (pendingProducts.length === 0) {
      setProductError('Add at least one product first.');
      return;
    }
    setSavingProduct(true);
    setProductError('');
    try {
      for (const item of pendingProducts) {
        await axios.post(`${API_URL}/quotes/${id}/products`, {
          product_id: item.product_id,
          quantity: item.quantity,
          price_per_unit: item.price_per_unit,
          description: item.description,
          uom_id: item.uom_id,
        }, { headers });
      }
      setOpenAddProductDialog(false);
      setPendingProducts([]);
      fetchQuoteDetail();
    } catch (error) {
      setProductError(error.response?.data?.message || 'Failed to add products.');
    } finally {
      setSavingProduct(false);
    }
  };

  const handleEditProduct = (item) => {
    setEditingProductId(item.quotedetailid);
    setProductError('');
    setSelectedProduct(null);
    setProductForm({
      quantity: item.quantity != null ? String(item.quantity) : '',
      price_per_unit: item.price_per_unit_raw != null ? String(item.price_per_unit_raw) : (item.price_per_unit != null ? String(item.price_per_unit) : ''),
      description: item.product_description || '',
      uom_id: item.uom_id || '',
      unit: item.unit || '',
    });
    fetchUoms();
  };

  const handleCancelEdit = () => {
    setEditingProductId(null);
    setProductForm({ quantity: '', price_per_unit: '', description: '', uom_id: '', unit: '' });
  };

  const handleSaveEdit = async (item) => {
    setSavingProduct(true);
    setProductError('');
    try {
      await axios.put(`${API_URL}/quotes/${id}/products/${item.quotedetailid}`, {
        description: productForm.description,
        quantity: productForm.quantity,
        price_per_unit: productForm.price_per_unit,
        uom_id: productForm.uom_id,
      }, { headers });
      setEditingProductId(null);
      fetchQuoteDetail();
    } catch (error) {
      setProductError(error.response?.data?.message || 'Failed to update product.');
    } finally {
      setSavingProduct(false);
    }
  };

  const handleDeleteProduct = (item) => {
    setDeleteProductTarget(item);
  };

  const confirmDeleteProduct = async () => {
    if (!deleteProductTarget) return;
    const item = deleteProductTarget;
    setDeletingProduct(true);
    setProductError('');
    try {
      await axios.delete(`${API_URL}/quotes/${id}/products/${item.quotedetailid}`, { headers });
      setDeleteProductTarget(null);
      fetchQuoteDetail();
    } catch (error) {
      setProductError(error.response?.data?.message || 'Failed to remove product.');
    } finally {
      setDeletingProduct(false);
    }
  };


  const handleStatusChange = (field, value) => {
    setStatusForm((prev) => {
      const next = { ...prev, [field]: value };
      // When Quote Status is no longer "Closed", clear closed status + reason
      if (field === 'quote_status' && value !== '100000003') {
        next.quote_closed_status = '';
        next.reason_if_lost = '';
      }
      // Clear dependent fields when parent changes
      if (field === 'quote_closed_status' && value !== '100000001') {
        next.reason_if_lost = '';  // Reason only applies when Lost
      }
      if (field === 'submit' && value !== '100000000') {
        next.head_approved = '';   // Head approval only when Submit = Yes
      }
      return next;
    });
  };

  const handleSaveStatus = async () => {
    setSavingStatus(true);
    setStatusError('');
    setStatusSuccess('');
    try {
      // Only send the Quote Status section fields the user is editing here.
      // "submit" and "head_approved" (Submit / Head Approval workflow) are
      // managed separately via handleWorkflowAutoSave, and head_approved is
      // restricted to Admin/Manager roles. Re-sending head_approved here would
      // incorrectly block a "User" role from changing New -> In Progress.
      await axios.put(`${API_URL}/quotes/${id}/status`, {
        quote_status: statusForm.quote_status,
        quote_closed_status: statusForm.quote_closed_status,
        reason_if_lost: statusForm.reason_if_lost,
      }, { headers });
      setStatusSuccess('Quote status updated successfully.');
      await fetchQuoteDetail();
      // If the quote was closed as "Won", offer to create a PM project.
      if (String(statusForm.quote_closed_status) === '100000000') {
        setCreateProjectError('');
        // Prefill the create-project form from the quote.
        const matchedCountry = countries.find(
          c => (c.name || '').toLowerCase() === (quote?.country || '').toLowerCase()
        );
        const matchedDivision = divisions.find(
          d => (d.name || '').toLowerCase() === (quote?.division || '').toLowerCase()
        );
        setCreateProjectData({
          name: quote?.name || quote?.quote_title || '',
          quote_ref: quote?.quote_ref || quote?.quote_number || '',
          start_date: '',
          due_date: '',
          country_id: matchedCountry?.id || '',
          division_id: matchedDivision?.id || '',
          description: quote?.description || quote?.opportunity_description || '',
          priority: 'Medium',
          quote_id: quote?.quoteid || '',
        });
        setCreateProjectDialogOpen(true);
      }
    } catch (error) {
      setStatusError(error.response?.data?.message || 'Failed to update quote status.');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleDateChange = (field, value) => {
    setDatesForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDates = async () => {
    setSavingDates(true);
    setDatesError('');
    setDatesSuccess('');
    try {
      await axios.put(`${API_URL}/quotes/${id}/dates`, {
        quote_requested_date: datesForm.quote_requested_date,
        quote_submission_date: datesForm.quote_submission_date,
        quote_due_date: datesForm.quote_due_date,
        quote_submission_type: datesForm.quote_submission_type,
      }, { headers });
      setDatesSuccess('Quote dates updated successfully.');
      fetchQuoteDetail();
    } catch (error) {
      setDatesError(error.response?.data?.message || 'Failed to update quote dates.');
    } finally {
      setSavingDates(false);
    }
  };

  const handleSaveQuote = async () => {
    await Promise.all([handleSaveDates(), handleSaveStatus()]);
  };

  const handleRevise = () => {
    setReviseError('');
    setReviseDialogOpen(true);
  };

  const confirmRevise = async () => {
    setRevising(true);
    setReviseError('');
    try {
      const res = await axios.post(`${API_URL}/quotes/${id}/revise`, {}, { headers });
      setReviseDialogOpen(false);
      const newId = res.data?.quoteid;
      if (newId) {
        window.location.href = `/quotes/${newId}`;
      } else {
        fetchQuoteDetail();
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to create quote revision.';
      const details = error.response?.data?.details || '';
      setReviseError(details ? `${msg}\n\n${details}` : msg);
    } finally {
      setRevising(false);
    }
  };

  const confirmCreateProjectFromQuote = async () => {
    if (!createProjectData.name || !createProjectData.country_id || !createProjectData.division_id) {
      setCreateProjectError('Please fill in the project name, country, and division.');
      return;
    }
    setCreatingProjectFromQuote(true);
    setCreateProjectError('');
    try {
      const res = await axios.post(`${API_URL}/projects`, createProjectData, { headers });
      setCreateProjectDialogOpen(false);
      const projectId = res.data?.id;
      if (projectId) {
        // Open the new project's details page automatically.
        window.location.href = `/projects/${projectId}`;
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to create project.';
      setCreateProjectError(msg);
    } finally {
      setCreatingProjectFromQuote(false);
    }
  };

  // Auto-save workflow (Submit / Head Approval) on every change
  const handleWorkflowAutoSave = async (field, value) => {
    // Normalize incoming value to a number (D365 option-set IDs are integers).
    const numValue = Number(value);

    const nextForm = { ...statusForm };
    const body = {};

    if (field === 'submit') {
      nextForm.submit = numValue;
      body.submit = numValue;
      if (numValue !== 100000000) {
        nextForm.head_approved = '';
      }
      // On Submit (Yes), automatically move a "New" quote to "In Progress".
      if (numValue === 100000000) {
        const currentStatus = Number(statusForm.quote_status ?? 0);
        // 100000000 = New, 100000001 = In Progress, 100000003 = Closed
        if (!currentStatus || currentStatus === 100000000) {
          nextForm.quote_status = 100000001;
          body.quote_status = 100000001;
        }
      }
    } else if (field === 'head_approved') {
      nextForm.head_approved = numValue;
      body.head_approved = numValue;
    }

    setStatusForm(nextForm);
    setSavingStatus(true);
    setStatusError('');
    try {
      await axios.put(`${API_URL}/quotes/${id}/status`, body, { headers });
      // Refresh from the server so the UI reflects the persisted submit value.
      await fetchQuoteDetail();
    } catch (error) {
      setStatusError(error.response?.data?.message || 'Failed to update workflow.');
    } finally {
      setSavingStatus(false);
    }
  };

  const fetchWinPrediction = async (quoteId) => {
    try {
      const res = await axios.post(`${API_URL}/quotes/batch-predict`, { quote_ids: [quoteId] }, { headers });
      const preds = res.data.predictions || {};
      const prediction = preds[(quoteId || '').toUpperCase()] || preds[quoteId] || null;
      setWinPrediction(prediction);
      // Capture shap_impacts from the prediction response
      if (prediction && prediction.shap_impacts) {
        setShapImpacts(prediction.shap_impacts);
      }
    } catch (err) {
      console.error('Win prediction fetch error:', err);
      setWinPrediction(null);
    }
  };

  const fetchAiAnalyze = async () => {
    setAiAnalyzeLoading(true);
    setAiAnalyzeError(null);
    setAiAnalyzeData(null);
    try {
      const predictionProb = winPrediction && winPrediction.win_probability != null
        ? winPrediction.win_probability / 100.0
        : 0.0;
      const payload = {
        quote_id: quote.quoteid,
        prediction_probability: predictionProb,
      };
      // Include SHAP impacts if available from batch-predict
      if (shapImpacts) {
        payload.shap_impacts = shapImpacts;
      }
      const res = await axios.post(`${API_URL}/ai-reports/analyze_quotation`, payload, { headers });
      if (res.data && res.data.status === 'success') {
        setAiAnalyzeData(res.data);
      } else {
        setAiAnalyzeError(res.data?.message || 'AI analysis returned an unexpected response.');
      }
    } catch (err) {
      setAiAnalyzeError(err.response?.data?.message || err.message || 'Failed to fetch AI analysis.');
    } finally {
      setAiAnalyzeLoading(false);
    }
  };

  const handleAddFollowup = async () => {
    if (!newFollowup) return;
    try {
      const reminder_date = (newFollowupStartDate && newFollowupStartTime) ? new Date(`${newFollowupStartDate}T${newFollowupStartTime}`).toISOString() : null;
      const date_time_end = (newFollowupEndDate && newFollowupEndTime) ? new Date(`${newFollowupEndDate}T${newFollowupEndTime}`).toISOString() : null;
      const next_visit = (newFollowupNextVisitStartDate && newFollowupNextVisitStartTime) ? new Date(`${newFollowupNextVisitStartDate}T${newFollowupNextVisitStartTime}`).toISOString() : null;
      const next_visit_end = (newFollowupNextVisitEndDate && newFollowupNextVisitEndTime) ? new Date(`${newFollowupNextVisitEndDate}T${newFollowupNextVisitEndTime}`).toISOString() : null;
      const payload = { note: newFollowup, description: newFollowupDesc, reminder_date, date_time_end, next_visit, next_visit_end, assigned_to_id: newFollowupAssignee || null };
      if (editingFollowupId) {
        await axios.put(`${API_URL}/quotes/${id}/followups/${editingFollowupId}`, payload, { headers });
      } else {
        await axios.post(`${API_URL}/quotes/${id}/followups`, payload, { headers });
      }
      resetFollowupForm();
      setOpenFollowupDialog(false);
      fetchFollowups();
    } catch (error) { console.error('Error adding/updating followup:', error); }
  };

  const resetFollowupForm = () => {
    setNewFollowup(''); setNewFollowupDesc(''); setNewFollowupStartDate(''); setNewFollowupStartTime('');
    setNewFollowupEndDate(''); setNewFollowupEndTime(''); setNewFollowupNextVisitStartDate(''); setNewFollowupNextVisitStartTime('');
    setNewFollowupNextVisitEndDate(''); setNewFollowupNextVisitEndTime(''); setNewFollowupAssignee(''); setEditingFollowupId(null);
  };

  const handleEditFollowup = (f) => {
    setNewFollowup(f.note);
    setNewFollowupDesc(f.description || '');
    if (f.reminder_date) { const d = new Date(f.reminder_date); setNewFollowupStartDate(d.toISOString().substring(0, 10)); setNewFollowupStartTime(d.toTimeString().substring(0, 5)); } else { setNewFollowupStartDate(''); setNewFollowupStartTime(''); }
    if (f.date_time_end) { const de = new Date(f.date_time_end); setNewFollowupEndDate(de.toISOString().substring(0, 10)); setNewFollowupEndTime(de.toTimeString().substring(0, 5)); } else { setNewFollowupEndDate(''); setNewFollowupEndTime(''); }
    if (f.next_visit) { const nv = new Date(f.next_visit); setNewFollowupNextVisitStartDate(nv.toISOString().substring(0, 10)); setNewFollowupNextVisitStartTime(nv.toTimeString().substring(0, 5)); } else { setNewFollowupNextVisitStartDate(''); setNewFollowupNextVisitStartTime(''); }
    if (f.next_visit_end) { const nve = new Date(f.next_visit_end); setNewFollowupNextVisitEndDate(nve.toISOString().substring(0, 10)); setNewFollowupNextVisitEndTime(nve.toTimeString().substring(0, 5)); } else { setNewFollowupNextVisitEndDate(''); setNewFollowupNextVisitEndTime(''); }
    setNewFollowupAssignee(f.assigned_to_id || '');
    setEditingFollowupId(f.id);
    setOpenFollowupDialog(true);
  };

  const handleAddConversation = async () => {
    if (!newConversation) return;
    try {
      await axios.post(`${API_URL}/quotes/${id}/conversations`, { message: newConversation }, { headers });
      setNewConversation('');
      fetchConversations();
    } catch (error) { console.error('Error adding conversation:', error); }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      await axios.post(`${API_URL}/quotes/${id}/documents`, { file_name: file.name, file_path: `/uploads/${file.name}` }, { headers });
      fetchDocuments();
    } catch (error) { console.error('Error uploading document:', error); }
    finally { setUploadingFile(false); }
  };

  const handleAddMilestone = async () => {
    if (!newMilestone) return;
    try {
      await axios.post(`${API_URL}/quotes/${id}/milestones`, { name: newMilestone, due_date: newMilestoneDate ? new Date(newMilestoneDate).toISOString() : null, status: 'Pending' }, { headers });
      setNewMilestone(''); setNewMilestoneDate(''); setOpenMilestoneDialog(false);
      fetchMilestones();
    } catch (error) { console.error('Error adding milestone:', error); }
  };

  const handleAssignUser = async () => {
    setAssignLoading(true);
    try {
      await axios.post(`${API_URL}/quotes/${id}/assignments`, { user_id: newAssignment }, { headers });
      setOpenAssignDialog(false); setNewAssignment('');
      fetchAssignments();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Error assigning user");
    } finally { setAssignLoading(false); }
  };

  if (loading || !quote) return <Loading message="Loading Quote Details..." />;

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('won')) return 'success';
    if (s.includes('lost')) return 'error';
    if (s.includes('active')) return 'primary';
    if (s.includes('draft')) return 'default';
    return 'info';
  };

  // AI Analyze tab is disabled when win % is not showing
  const isWinPercentShowing =
    quote?.statecode === 2 ||
    quote?.statecode === 3 ||
    (winPrediction && winPrediction.win_probability != null && !winPrediction.inactive);

  // A closed quote is READ-ONLY — no data can be edited.
  // In Dynamics 365 a quote is "closed" when it is Won (statecode 2) or Lost
  // (statecode 3), or has a Closed/Cancelled status label.
  const isQuoteClosed =
    quote?.statecode === 2 ||
    quote?.statecode === 3 ||
    /closed|won|lost|cancelled|canceled/i.test(quote?.status || '') ||
    (quote?.closed_status || '').toLowerCase().includes('closed');

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      {/* Modern Gradient Header */}
      <Grow in={true} timeout={500}>
        <Paper elevation={0} sx={{ mb: 4, mt: 2, p: 4, borderRadius: 4, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white', boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)', position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
          <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'flex-start' }, justifyContent: 'space-between', gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                  <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/quotes')} sx={{ color: '#90caf9', pl: 0, fontWeight: 'bold', '&:hover': { color: 'white', bgcolor: 'transparent', transform: 'translateX(-4px)' }, transition: 'all 0.2s' }}>Back to Quotes</Button>
                  <Button
                    variant="contained"
                    startIcon={<AutorenewIcon />}
                    disabled={revising}
                    onClick={handleRevise}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 'bold',
                      px: 2.5,
                      py: 1,
                      boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      '&:hover': { background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)', boxShadow: '0 6px 20px rgba(0,0,0,0.3)', transform: 'translateY(-2px)' },
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {revising ? 'Revising…' : 'Revision'}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <Avatar sx={{ width: 52, height: 52, flexShrink: 0, bgcolor: 'rgba(255,255,255,0.15)', color: '#e3f2fd', boxShadow: '0 6px 18px rgba(0,0,0,0.2)', border: '2px solid rgba(255,255,255,0.2)' }}>
                    <RequestQuoteIcon sx={{ color: 'white', fontSize: 26 }} />
                  </Avatar>
                  <Typography variant="h5" fontWeight="900" sx={{ letterSpacing: 0.3, wordBreak: 'break-word', color: 'white', lineHeight: 1.3 }}>{quote.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', ml: 0.5, mt: 1.5 }}>
                  <Box sx={{ 
                    display: 'flex', alignItems: 'center', gap: 0.8, 
                    bgcolor: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)',
                    px: 1.8, py: 0.7, borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.2)',
                  }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: (quote.statecode === 2 || (quote.status || '').toLowerCase().includes('won')) ? '#4ade80' : (quote.statecode === 3 || (quote.status || '').toLowerCase().includes('lost')) ? '#f87171' : '#facc15', boxShadow: `0 0 6px ${(quote.statecode === 2 || (quote.status || '').toLowerCase().includes('won')) ? '#4ade80' : (quote.statecode === 3 || (quote.status || '').toLowerCase().includes('lost')) ? '#f87171' : '#facc15'}` }} />
                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, letterSpacing: 0.3, fontSize: '0.75rem' }}>
                      {quote.status || 'Draft'}
                    </Typography>
                  </Box>
                  {quote.customer_name && (
                      <Box sx={{ 
                        display: 'flex', alignItems: 'center', gap: 0.8, 
                        bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
                        px: 1.8, py: 0.7, borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.12)',
                      }}>
                        <BusinessIcon sx={{ color: '#93c5fd', fontSize: 14 }} />
                        <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.75rem' }}>
                          {quote.customer_name}
                        </Typography>
                      </Box>
                  )}
                  {quote.quote_ref && (
                      <Box sx={{ 
                        display: 'flex', alignItems: 'center', gap: 0.8, 
                        bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
                        px: 1.8, py: 0.7, borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.12)',
                      }}>
                        <RequestQuoteIcon sx={{ color: '#c4b5fd', fontSize: 14 }} />
                        <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.75rem' }}>
                          {quote.quote_ref}
                        </Typography>
                      </Box>
                  )}
                  {quote.quote_number && (
                      <Box sx={{ 
                        display: 'flex', alignItems: 'center', gap: 0.8, 
                        bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
                        px: 1.8, py: 0.7, borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.12)',
                      }}>
                        <AssignmentIcon sx={{ color: '#fde68a', fontSize: 14 }} />
                        <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.75rem' }}>
                          {quote.quote_number}
                        </Typography>
                      </Box>
                  )}
                  {quote.country && (
                      <Box sx={{ 
                        display: 'flex', alignItems: 'center', gap: 0.8, 
                        bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
                        px: 1.8, py: 0.7, borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.12)',
                      }}>
                        <PublicIcon sx={{ color: '#86efac', fontSize: 14 }} />
                        <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.75rem' }}>
                          {quote.country}
                        </Typography>
                      </Box>
                  )}
                  {quote.division && (
                      <Box sx={{ 
                        display: 'flex', alignItems: 'center', gap: 0.8, 
                        bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
                        px: 1.8, py: 0.7, borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.12)',
                      }}>
                        <FolderIcon sx={{ color: '#fde68a', fontSize: 14 }} />
                        <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.75rem' }}>
                          {quote.division}
                        </Typography>
                      </Box>
                  )}
                  {isWinPercentShowing && (
                      <Box sx={{ 
                        display: 'flex', alignItems: 'center', gap: 0.8, 
                        bgcolor: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)',
                        px: 1.8, py: 0.7, borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}>
                        <CheckCircleIcon sx={{ color: (quote.statecode === 2 || (winPrediction && winPrediction.win_probability >= 50)) ? '#4ade80' : '#f87171', fontSize: 14 }} />
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, letterSpacing: 0.3, fontSize: '0.75rem' }}>
                          {quote.statecode === 2 ? 'Won 100%' : quote.statecode === 3 ? 'Lost 0%' : `Win: ${winPrediction?.win_probability}%`}
                        </Typography>
                      </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Grow>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', minHeight: '400px', bgcolor: 'white', border: '1px solid #f0f0f0' }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} variant="scrollable" scrollButtons="auto" TabIndicatorProps={{ sx: { height: 3, borderRadius: '3px 3px 0 0' } }} sx={{ borderBottom: 1, borderColor: '#f0f0f0', bgcolor: 'white', px: 2, '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', minHeight: 64, fontSize: '0.95rem' } }}>
              <Tab icon={<InfoIcon />} label="Quote Details" iconPosition="start" />
              <Tab icon={<ShoppingCartIcon />} label="Products" iconPosition="start" />
              <Tab icon={<CalendarTodayIcon />} label="Followups" iconPosition="start" />
              <Tab icon={<ForumIcon />} label="Conversations" iconPosition="start" />
              <Tab icon={<CloudUploadIcon />} label="Documents" iconPosition="start" />
              <Tab icon={<PersonAddIcon />} label="Assignments" iconPosition="start" />
              <Tab icon={<FlagIcon />} label="Milestones" iconPosition="start" />
              <Tab icon={<PsychologyIcon />} label="AI Analyze" iconPosition="start" disabled={!isWinPercentShowing} />
            </Tabs>

                                    {/* Tab 0: Quote Details */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ px: 2.5, py: 1.5, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 4px 16px rgba(0,0,0,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                        <WorkflowNode
                          label="Submit"
                          active
                          disabled={isQuoteClosed}
                          status={Number(statusForm.submit) === 100000000 ? 'yes' : (statusForm.submit == null || statusForm.submit === '' ? 'pending' : 'no')}
                          onClick={() => {
                            if (Number(statusForm.submit) !== 100000000) {
                              handleWorkflowAutoSave('submit', 100000000);
                            }
                          }}
                        />
                        <WorkflowArrow active={Number(statusForm.submit) === 100000000} />
                        <WorkflowNode
                          label="Head Approval"
                          disabled={isQuoteClosed || Number(statusForm.submit) !== 100000000 || !canApproveHead}
                          active={Number(statusForm.submit) === 100000000}
                          status={Number(statusForm.head_approved) === 100000000 ? 'yes' : (statusForm.head_approved == null || statusForm.head_approved === '' ? 'pending' : 'no')}
                          onClick={() => {
                            if (canApproveHead && !isQuoteClosed && Number(statusForm.head_approved) !== 100000000) {
                              handleWorkflowAutoSave('head_approved', 100000000);
                            }
                          }}
                        />
                      </Box>
                      {savingStatus && <CircularProgress size={18} />}
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card elevation={0} sx={{ borderRadius: 4, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}><RequestQuoteIcon sx={{ color: '#3b82f6' }} /><Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Quote Summary</Typography></Box>
                        <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Quote Title</Typography><Typography variant="body1" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.5 }}>{quote.quote_title || quote.name || '—'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Client Name</Typography><Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.5 }}>{quote.customer_name || 'N/A'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Account Number</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#2563eb', mt: 0.5 }}>{quote.account_number || '—'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Subtotal</Typography><Typography variant="body1" sx={{ fontWeight: 700, color: '#334155', mt: 0.5 }}>{formatCurrency(quote.subtotal)}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Tax</Typography><Typography variant="body1" sx={{ fontWeight: 700, color: '#f59e0b', mt: 0.5 }}>{quote.tax_percentage > 0 ? `${quote.tax_percentage}%` : formatCurrency(quote.total_tax)}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Total Amount</Typography><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}><AttachMoneyIcon sx={{ fontSize: 22, color: '#10b981' }} /><Typography variant="h6" sx={{ fontWeight: '800', color: '#10b981' }}>{formatCurrency(quote.total_amount)}</Typography></Box></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Status</Typography><Box sx={{ mt: 0.5 }}><Chip label={quote.status || 'Draft'} color={getStatusColor(quote.status)} size="small" sx={{ fontWeight: 'bold', borderRadius: 1.5 }} /></Box></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Created On</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#334155', mt: 0.5 }}>{formatDate(quote.createdon)}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Quote Ref</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#2563eb', mt: 0.5 }}>{quote.quote_ref || quote.quote_number || '—'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Revision</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#334155', mt: 0.5 }}>{quote.revision_number != null && quote.revision_number !== '' ? quote.revision_number : '—'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Country</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#334155', mt: 0.5 }}>{quote.country || '—'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Division</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#334155', mt: 0.5 }}>{quote.division || '—'}</Typography></Grid>
                            <Grid item xs={12}><Divider sx={{ borderColor: '#e2e8f0' }} /></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Grand Total</Typography><Typography variant="h6" sx={{ fontWeight: '800', color: '#0f172a', mt: 0.5 }}>{formatCurrency(quote.grand_total)}</Typography></Grid>
                            {quote.total_discount > 0 && <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Discount</Typography><Typography variant="body1" sx={{ fontWeight: 700, color: '#ef4444', mt: 0.5 }}>-{formatCurrency(quote.total_discount)}</Typography></Grid>}
                          </Grid>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>


                  <Grid item xs={12} md={6}>
                    <Card elevation={0} sx={{ borderRadius: 4, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}><DateRangeIcon sx={{ color: '#3b82f6' }} /><Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Quote Dates</Typography></Box>
                        {datesError && <Alert severity="error" onClose={() => setDatesError('')} sx={{ mb: 2 }}>{datesError}</Alert>}
                        {datesSuccess && <Alert severity="success" onClose={() => setDatesSuccess('')} sx={{ mb: 2 }}>{datesSuccess}</Alert>}
                        <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Created On</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#334155', mt: 0.5 }}>{formatDate(quote.createdon)}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Quote Due Date</Typography><TextField type="date" size="small" fullWidth value={datesForm.quote_due_date} onChange={(e) => handleDateChange('quote_due_date', e.target.value)} disabled={isQuoteClosed} sx={{ mt: 0.5, bgcolor: 'white', borderRadius: 2 }} InputLabelProps={{ shrink: true }} /></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Quote Requested Date</Typography><TextField type="date" size="small" fullWidth value={datesForm.quote_requested_date} onChange={(e) => handleDateChange('quote_requested_date', e.target.value)} disabled={isQuoteClosed} sx={{ mt: 0.5, bgcolor: 'white', borderRadius: 2 }} InputLabelProps={{ shrink: true }} /></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Quote Submission Date</Typography><TextField type="date" size="small" fullWidth value={datesForm.quote_submission_date} onChange={(e) => handleDateChange('quote_submission_date', e.target.value)} disabled={isQuoteClosed} sx={{ mt: 0.5, bgcolor: 'white', borderRadius: 2 }} InputLabelProps={{ shrink: true }} /></Grid>
                            <Grid item xs={12}><Divider sx={{ borderColor: '#e2e8f0' }} /></Grid>
                            <Grid item xs={12}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Quote Submission Type</Typography><FormControl fullWidth sx={{ mt: 1 }}><Select value={datesForm.quote_submission_type ?? ''} onChange={(e) => handleDateChange('quote_submission_type', e.target.value)} displayEmpty disabled={isQuoteClosed} sx={{ borderRadius: 2, bgcolor: 'white' }}><MenuItem value=""><em>Select Submission Type</em></MenuItem><MenuItem value={100000000}>Option 1</MenuItem><MenuItem value={100000001}>Option 2</MenuItem><MenuItem value={100000002}>Option 3</MenuItem></Select></FormControl></Grid>
                          </Grid>
                        </Box>

                        <Divider sx={{ my: 3, borderColor: '#e2e8f0' }} />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}><FlagIcon sx={{ color: '#3b82f6' }} /><Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b' }}>Quote Status</Typography></Box>
                        {statusError && <Alert severity="error" onClose={() => setStatusError('')} sx={{ mb: 2 }}>{statusError}</Alert>}
                        {statusSuccess && <Alert severity="success" onClose={() => setStatusSuccess('')} sx={{ mb: 2 }}>{statusSuccess}</Alert>}
                        <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Quote Status</Typography>
                              <FormControl fullWidth sx={{ mt: 1 }}>
                                <Select value={statusForm.quote_status ?? ''} onChange={(e) => handleStatusChange('quote_status', e.target.value)} displayEmpty disabled={isQuoteClosed} sx={{ borderRadius: 2, bgcolor: 'white' }}>
                                  <MenuItem value={100000000}>New</MenuItem>
                                  <MenuItem value={100000001}>In Progress</MenuItem>
                                  <MenuItem value={100000003}>Closed</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            {String(statusForm.quote_status) === '100000003' && (
                              <Grid item xs={12}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Quote Closed Status</Typography>
                                <FormControl fullWidth sx={{ mt: 1 }}>
                                  <Select value={statusForm.quote_closed_status ?? ''} onChange={(e) => handleStatusChange('quote_closed_status', e.target.value)} displayEmpty disabled={isQuoteClosed} sx={{ borderRadius: 2, bgcolor: 'white' }}>
                                    <MenuItem value={100000000}>Won</MenuItem>
                                    <MenuItem value={100000001}>Lost</MenuItem>
                                    <MenuItem value={100000002}>Under Bidding</MenuItem>
                                    <MenuItem value={100000003}>Revision</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                            )}
                            {String(statusForm.quote_closed_status) === '100000001' && (
                              <Grid item xs={12}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Reason If Lost</Typography>
                                <FormControl fullWidth sx={{ mt: 1 }}>
                                  <Select value={statusForm.reason_if_lost ?? ''} onChange={(e) => handleStatusChange('reason_if_lost', e.target.value)} displayEmpty disabled={isQuoteClosed} sx={{ borderRadius: 2, bgcolor: 'white' }}>
                                    <MenuItem value={100000000}>Financial Reason</MenuItem>
                                    <MenuItem value={100000001}>Cancelled By Client</MenuItem>
                                    <MenuItem value={100000002}>Bad Political Situation</MenuItem>
                                    <MenuItem value={100000003}>Bad Experience with ACTS Service</MenuItem>
                                    <MenuItem value={100000004}>No Follow-up by ACTS Employee</MenuItem>
                                    <MenuItem value={100000005}>Competitive Offer</MenuItem>
                                    <MenuItem value={100000009}>Won to ACTS by Another Account</MenuItem>
                                    <MenuItem value={100000010}>Revision</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                          <Button variant="contained" onClick={handleSaveQuote} disabled={isQuoteClosed || savingDates || savingStatus} sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, boxShadow: 'none', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', '&:hover': { boxShadow: '0 4px 12px rgba(25,118,210,0.3)' } }}>
                            {(savingDates || savingStatus) ? <CircularProgress size={24} color="inherit" /> : 'Save'}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {quote.description && (
                    <Grid item xs={12} md={6}>
                      <Card elevation={0} sx={{ borderRadius: 4, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}><DescriptionIcon sx={{ color: '#3b82f6' }} /><Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Quote Description</Typography></Box>
                          <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0' }}><Typography variant="body2" sx={{ color: '#475569', whiteSpace: 'pre-wrap' }}>{quote.description}</Typography></Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Card elevation={0} sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                      <CardContent sx={{ p: 3 }}>
                        {qrData?.qr_code ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                            <Box sx={{ p: 1, bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: 2, flexShrink: 0 }}>
                              <img src={`data:image/png;base64,${qrData.qr_code}`} alt="Quote QR Code" style={{ width: 150, height: 150, display: 'block' }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 240 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <QrCode2Icon sx={{ color: '#3b82f6' }} />
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Quote QR Code</Typography>
                              </Box>
                              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>QR Contains</Typography>
                              <Box sx={{ mt: 1, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', p: 2 }}>
                                <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-line', fontFamily: 'monospace', fontWeight: 600 }}>{qrData.content}</Typography>
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ textAlign: 'center', color: '#94a3b8' }}>
                            <QrCode2Icon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                            <Typography variant="body2">QR code could not be generated for this quote.</Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>

{/* Tab 1: Products */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <ShoppingCartIcon sx={{ color: '#3b82f6' }} />
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                      Quote Products ({quote.line_items ? quote.line_items.length : 0})
                    </Typography>
                  </Box>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddProduct} disabled={isQuoteClosed} sx={{ borderRadius: 2, fontWeight: 'bold', px: 3, boxShadow: '0 4px 14px rgba(25,118,210,0.3)', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(25,118,210,0.4)', background: 'linear-gradient(45deg, #1565c0 0%, #1e88e5 100%)' } }}>Add Product</Button>
                </Box>
                {productError && <Alert severity="error" onClose={() => setProductError('')}>{productError}</Alert>}
                {quote.line_items && quote.line_items.length > 0 ? (
                  <Grid item xs={12}>
                    <Card elevation={0} sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                      <CardContent sx={{ p: 4 }}>
                        <TableContainer>
                          <Table size="small">
                            <TableHead sx={{ bgcolor: '#f8fafc' }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem' }}>Product</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem' }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem' }} align="right">Qty</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem' }} align="right">Unit</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem' }} align="right">Price/Unit</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem' }} align="right">Amount</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem' }} align="right">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {quote.line_items.map((item, idx) => {
                                const isEditing = editingProductId === item.quotedetailid;
                                return (
                                  <TableRow key={item.quotedetailid || idx} hover>
                                    <TableCell sx={{ fontWeight: 600, color: '#334155' }}>{item.product_name || '—'}</TableCell>
                                    <TableCell sx={{ color: '#64748b', minWidth: 160 }}>
                                      {isEditing ? (
                                        <TextField size="small" fullWidth value={productForm.description} onChange={(e) => handleProductFormChange('description', e.target.value)} />
                                      ) : (
                                        <Typography variant="body2" sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.product_description || '—'}</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#334155', width: 90 }}>
                                      {isEditing ? (
                                        <TextField size="small" type="number" inputProps={{ min: 0, step: 'any', style: { textAlign: 'right' } }} value={productForm.quantity} onChange={(e) => handleProductFormChange('quantity', e.target.value)} />
                                      ) : (
                                        item.quantity ?? '—'
                                      )}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#334155', minWidth: 140 }}>
                                      {isEditing ? (
                                        <FormControl size="small" sx={{ minWidth: 130 }}>
                                          <Select
                                            value={productForm.uom_id || ''}
                                            onChange={(e) => handleProductFormChange('uom_id', e.target.value)}
                                            displayEmpty
                                          >
                                            {uomOptions.map((u) => (
                                              <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      ) : (
                                        item.unit || '—'
                                      )}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#334155', width: 120 }}>
                                      {isEditing ? (
                                        <TextField size="small" type="number" inputProps={{ min: 0, step: 'any', style: { textAlign: 'right' } }} value={productForm.price_per_unit} onChange={(e) => handleProductFormChange('price_per_unit', e.target.value)} />
                                      ) : (
                                        item.price_per_unit ? formatCurrency(item.price_per_unit) : '—'
                                      )}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: '#10b981' }}>{item.extended_amount ? formatCurrency(item.extended_amount) : '—'}</TableCell>
                                    <TableCell align="right">
                                      {isEditing ? (
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                          <Tooltip title="Save"><IconButton size="small" color="primary" onClick={() => handleSaveEdit(item)} disabled={savingProduct}><SaveIcon fontSize="small" /></IconButton></Tooltip>
                                          <Tooltip title="Cancel"><IconButton size="small" onClick={handleCancelEdit}><CloseIcon fontSize="small" /></IconButton></Tooltip>
                                        </Box>
                                      ) : (
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                          <Tooltip title="Edit"><IconButton size="small" onClick={() => handleEditProduct(item)} disabled={isQuoteClosed}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteProduct(item)} disabled={isQuoteClosed}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                        </Box>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                ) : (
                  <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: '#f8fafc' }}>
                    <ShoppingCartIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                    <Typography variant="h6" color="#64748b" fontWeight="700" mb={1}>No Products</Typography>
                    <Typography variant="body2" color="#94a3b8">Add products to this quote using the button above.</Typography>
                  </Paper>
                )}
              </Box>
            </TabPanel>

            {/* Tab 2: Followups */}
            <TabPanel value={tabValue} index={2}>
              <Box sx={{ mt: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h5" fontWeight="800" color="#1e293b">Quote Follow-Ups</Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetFollowupForm(); setOpenFollowupDialog(true); }} disabled={isQuoteClosed} sx={{ borderRadius: 2, fontWeight: 'bold', px: 3, boxShadow: '0 4px 14px rgba(25,118,210,0.3)', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(25,118,210,0.4)', background: 'linear-gradient(45deg, #1565c0 0%, #1e88e5 100%)' } }}>Log Follow-up</Button>
                </Box>
                <Box>
                  {quoteFollowups.length === 0 ? (
                    <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: "#f8fafc" }}>
                      <EventNoteIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                      <Typography variant="h6" color="#64748b" fontWeight="700" mb={1}>No follow-ups recorded</Typography>
                      <Typography variant="body2" color="#94a3b8">Log your interactions and upcoming activities with this quote.</Typography>
                    </Paper>
                  ) : (
                    <Box sx={{ position: 'relative', pl: { xs: 2.5, md: 5 }, pr: { xs: 1, md: 3 }, '&::before': { content: '""', position: 'absolute', top: 0, bottom: 0, left: { xs: '10px', md: '20px' }, width: '3px', bgcolor: '#e2e8f0', borderRadius: 3, zIndex: 0 }, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {quoteFollowups.map(item => {
                        const isAuto = item.note && item.note.startsWith("[Next Visit]");
                        return (
                          <Box key={item.id} sx={{ position: 'relative', zIndex: 1, mb: 1 }}>
                            <Box sx={{ position: 'absolute', left: { xs: -24, md: -45 }, top: 24, width: 18, height: 18, borderRadius: '50%', background: isAuto ? 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', border: '4px solid #ffffff', boxShadow: '0 0 0 2px #e2e8f0' }} />
                            <Card elevation={0} sx={{ border: '1px solid', borderColor: isAuto ? '#ede9fe' : '#e2e8f0', borderRadius: 4, transition: 'all 0.3s ease', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', '&:hover': { borderColor: isAuto ? '#c4b5fd' : '#cbd5e1', boxShadow: '0 10px 30px rgba(0,0,0,0.06)', transform: 'translateY(-3px)' }, overflow: 'hidden' }}>
                              <Box sx={{ p: 2.5, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, borderBottom: '1px solid', borderColor: isAuto ? '#ede9fe' : '#f1f5f9', background: isAuto ? 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar sx={{ bgcolor: isAuto ? '#8b5cf6' : '#2563eb', color: 'white', width: 44, height: 44, boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>{isAuto ? <AccessTimeIcon /> : <EventNoteIcon />}</Avatar>
                                  <Box>
                                    <Typography variant="subtitle1" fontWeight="800" color="#1e293b">
                                      {item.note && isAuto ? item.note.replace('[Next Visit] ', '') : (item.created_at ? new Date(item.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'No Start Date')}
                                    </Typography>
                                    <Box sx={{ mt: 0.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                      {isAuto ? <Chip label="Auto-Generated" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, bgcolor: '#e0e7ff', color: '#4f46e5' }} /> : null}
                                      {item.assigned_to_name && <Typography variant="caption" color="#64748b" fontWeight="600">Assignee: <span style={{ color: '#334155' }}>{item.assigned_to_name}</span></Typography>}
                                      <Typography variant="caption" color="#94a3b8" fontWeight="500">• Logged by: {item.created_by_name || 'System'}</Typography>
                                    </Box>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}><Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => handleEditFollowup(item)} disabled={isQuoteClosed} sx={{ borderRadius: 2, fontWeight: 'bold', borderColor: '#cbd5e1', bgcolor: 'white', color: '#475569', '&:hover': { bgcolor: '#f8fafc' } }}>Edit</Button></Box>
                              </Box>
                              <CardContent sx={{ p: 3, bgcolor: 'white' }}>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: '#334155', lineHeight: 1.6, fontSize: '1rem', mb: 3 }}>{item.description ? (!isAuto ? item.description : item.description.replace('[Next Visit] ', '')) : 'No additional details provided.'}</Typography>
                                {(item.reminder_date || item.date_time_end || item.next_visit || item.next_visit_end) && (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, pt: 2.5, borderTop: '1px dashed #e2e8f0' }}>
                                    {item.reminder_date && <Chip icon={<DateRangeIcon fontSize="small" sx={{ color: '#d97706 !important' }} />} label={`Start: ${new Date(item.reminder_date).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}`} sx={{ bgcolor: '#fffbeb', color: '#d97706', fontWeight: 600, borderRadius: 1.5, px: 1, border: '1px solid #fef3c7' }} />}
                                    {item.date_time_end && <Chip icon={<AccessTimeIcon fontSize="small" sx={{ color: '#d97706 !important' }} />} label={`End: ${new Date(item.date_time_end).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}`} sx={{ bgcolor: '#fffbeb', color: '#d97706', fontWeight: 600, borderRadius: 1.5, px: 1, border: '1px solid #fef3c7' }} />}
                                    {(item.next_visit || item.next_visit_end) && <Chip icon={<AccessTimeIcon fontSize="small" sx={{ color: '#7c3aed !important' }} />} label={`Next Visit: ${item.next_visit ? new Date(item.next_visit).toLocaleDateString() : ''} ${item.next_visit_end ? '- ' + new Date(item.next_visit_end).toLocaleDateString() : ''}`} sx={{ bgcolor: '#f5f3ff', color: '#6d28d9', fontWeight: 700, borderRadius: 1.5, px: 1, border: '1px solid #ede9fe' }} />}
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            </TabPanel>

            {/* Tab 3: Conversations */}
            <TabPanel value={tabValue} index={3}>
              <Box sx={{ mt: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h5" fontWeight="800" color="#1e293b">Quote Discussion</Typography>
                </Box>
                <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 4, display: 'flex', flexDirection: 'column', height: 600, bgcolor: "#f8fafc", overflow: 'hidden' }}>
                  <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, md: 4 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {quoteConversations.length === 0 && (
                      <Box sx={{ textAlign: 'center', my: 'auto', color: '#94a3b8' }}>
                        <ForumIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                        <Typography variant="h6" fontWeight="700" color="#64748b">No discussions yet</Typography>
                        <Typography variant="body2">Be the first to start the conversation.</Typography>
                      </Box>
                    )}
                    {quoteConversations.map((msg) => (
                      <Box key={msg.id} sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, '&:hover .msg-time': { opacity: 1 } }}>
                        <Avatar sx={{ bgcolor: "#3b82f6", width: 40, height: 40, boxShadow: '0 2px 8px rgba(59,130,246,0.3)', fontWeight: 'bold' }}>{msg.created_by_name ? msg.created_by_name.charAt(0).toUpperCase() : 'U'}</Avatar>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '80%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5, ml: 1 }}>
                            <Typography variant="subtitle2" fontWeight="700" color="#334155">{msg.created_by_name || 'User'}</Typography>
                            <Typography className="msg-time" variant="caption" color="#94a3b8" sx={{ opacity: 0.7, transition: 'opacity 0.2s' }}>{new Date(msg.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</Typography>
                          </Box>
                          <Paper sx={{ p: 2.5, borderRadius: '20px 20px 20px 4px', bgcolor: 'white', color: '#1e293b', boxShadow: '0 4px 14px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9' }}><Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '0.95rem' }}>{msg.message}</Typography></Paper>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ p: 2.5, bgcolor: 'white', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField fullWidth variant="outlined" placeholder="Type a message..." value={newConversation} onChange={(e) => setNewConversation(e.target.value)} disabled={isQuoteClosed} onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddConversation(); } }} multiline maxRows={4} InputProps={{ sx: { borderRadius: 3, bgcolor: "#f8fafc", '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: '#cbd5e1' }, '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '2px' } } }} />
                    <IconButton onClick={handleAddConversation} disabled={isQuoteClosed || !newConversation.trim()} sx={{ bgcolor: "#3b82f6", color: 'white', width: 48, height: 48, boxShadow: '0 4px 12px rgba(59,130,246,0.3)', transition: 'all 0.2s ease', '&:hover': { bgcolor: '#2563eb', transform: 'scale(1.05)' }, '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' } }}><SendIcon /></IconButton>
                  </Box>
                </Paper>
              </Box>
            </TabPanel>

            {/* Tab 4: Documents */}
            <TabPanel value={tabValue} index={4}>
              <Box sx={{ mt: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h5" fontWeight="800" color="#1e293b">Quote Documents</Typography>
                  <Button variant="contained" startIcon={uploadingFile ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />} component="label" disabled={isQuoteClosed || uploadingFile} sx={{ borderRadius: 2, fontWeight: 'bold', px: 3, boxShadow: '0 4px 14px rgba(25,118,210,0.3)', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(25,118,210,0.4)', background: 'linear-gradient(45deg, #1565c0 0%, #1e88e5 100%)' }, '&.Mui-disabled': { background: '#94a3b8', color: '#f1f5f9' } }}>{uploadingFile ? 'Uploading...' : 'Upload Document'}<input type="file" hidden onChange={handleFileChange} disabled={isQuoteClosed} /></Button>
                </Box>
                {quoteDocuments.length === 0 ? (
                  <Paper elevation={0} component="label" sx={{ p: 8, textAlign: 'center', bgcolor: "#f8fafc", borderRadius: 4, border: '2px dashed #cbd5e1', cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { borderColor: '#3b82f6', bgcolor: '#eff6ff', transform: 'translateY(-2px)' } }}>
                    <CloudUploadIcon sx={{ fontSize: 64, color: '#94a3b8', mb: 2 }} />
                    <Typography variant="h6" color="#334155" fontWeight="700" gutterBottom>No documents found</Typography>
                    <Typography variant="body2" color="#64748b" sx={{ mb: 3 }}>Click here to upload specifications, contracts, or other quote files.</Typography>
                    <Button variant="contained" component="span" sx={{ borderRadius: 2, fontWeight: 'bold', px: 4 }}>Browse Files</Button>
                    <input type="file" hidden onChange={handleFileChange} />
                  </Paper>
                ) : (
                  <Grid container spacing={3}>
                    {quoteDocuments.map((doc) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
                        <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 4, display: 'flex', flexDirection: 'column', height: '100%', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', bgcolor: 'white', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 12px 24px rgba(0,0,0,0.06)', borderColor: '#93c5fd' } }}>
                          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', pt: 4, pb: 2 }}>
                            <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6', width: 72, height: 72, mb: 2.5, boxShadow: '0 4px 12px rgba(59,130,246,0.15)' }}><InsertDriveFileIcon sx={{ fontSize: 36 }} /></Avatar>
                            <Tooltip title={doc.file_name} arrow placement="top"><Typography variant="subtitle1" fontWeight="700" color="#1e293b" noWrap sx={{ width: '100%', mb: 1, px: 2 }}>{doc.file_name}</Typography></Tooltip>
                            <Typography variant="caption" color="#94a3b8" fontWeight="600">{new Date(doc.uploaded_at || Date.now()).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</Typography>
                          </CardContent>
                          <Divider sx={{ borderColor: '#f1f5f9' }} />
                          <CardActions sx={{ justifyContent: 'center', p: 0 }}>
                            <Button color="primary" startIcon={<DownloadIcon />} fullWidth sx={{ py: 1.5, borderRadius: '0 0 16px 16px', fontWeight: '700', letterSpacing: 0.5, color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }} href={`${API_URL}/quotes/${id}/documents/${doc.id}/download`} target="_blank" rel="noopener noreferrer">Download</Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </TabPanel>

            {/* Tab 5: Assignments */}
            <TabPanel value={tabValue} index={5}>
              <Box sx={{ mt: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h5" fontWeight="800" color="#1e293b">Team Members</Typography>
                  <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setOpenAssignDialog(true)} disabled={isQuoteClosed} sx={{ borderRadius: 2, fontWeight: 'bold', px: 3, boxShadow: '0 4px 14px rgba(25,118,210,0.3)', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(25,118,210,0.4)', background: 'linear-gradient(45deg, #1565c0 0%, #1e88e5 100%)' } }}>Add Member</Button>
                </Box>
                {quoteAssignments.length === 0 ? (
                  <Paper elevation={0} sx={{ p: 8, textAlign: 'center', bgcolor: "#f8fafc", borderRadius: 4, border: '2px dashed #cbd5e1' }}>
                    <PersonAddIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                    <Typography variant="h6" color="#334155" fontWeight="700" gutterBottom>No team members assigned</Typography>
                    <Typography variant="body2" color="#64748b" sx={{ mb: 3 }}>Assign users to this quote to track responsibilities.</Typography>
                    <Button variant="outlined" onClick={() => setOpenAssignDialog(true)} sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, borderWidth: 2, '&:hover': { borderWidth: 2 } }}>Assign Someone</Button>
                  </Paper>
                ) : (
                  <Grid container spacing={3}>
                    {quoteAssignments.map((assignment) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={assignment.id || assignment.user_id}>
                        <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', bgcolor: 'white', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 12px 24px rgba(0,0,0,0.06)', borderColor: '#93c5fd' } }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1, mb: 3 }}>
                              <Avatar sx={{ bgcolor: '#e0e7ff', color: '#2563eb', fontWeight: '800', width: 64, height: 64, mb: 1, fontSize: '1.5rem', boxShadow: '0 4px 12px rgba(37,99,235,0.15)' }}>{assignment.user_name ? assignment.user_name[0].toUpperCase() : 'U'}</Avatar>
                              <Typography variant="h6" fontWeight="800" color="#1e293b" noWrap sx={{ width: '100%' }}>{assignment.user_name || `User ID: ${assignment.user_id}`}</Typography>
                              <Chip label="Team Member" size="small" sx={{ height: 24, fontSize: '0.75rem', fontWeight: 700, bgcolor: '#f1f5f9', color: '#475569' }} />
                            </Box>
                            <Divider sx={{ borderColor: '#f8fafc', mb: 2 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccessTimeIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                <Typography variant="caption" color="#64748b" fontWeight="600" letterSpacing={0.5}>ASSIGNED: {new Date(assignment.assigned_at || Date.now()).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </TabPanel>

            {/* Tab 6: Milestones */}
            <TabPanel value={tabValue} index={6}>
              <Box sx={{ mt: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h5" fontWeight="800" color="#1e293b">Quote Milestones</Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenMilestoneDialog(true)} disabled={isQuoteClosed} sx={{ borderRadius: 2, fontWeight: 'bold', px: 3, boxShadow: '0 4px 14px rgba(25,118,210,0.3)', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(25,118,210,0.4)', background: 'linear-gradient(45deg, #1565c0 0%, #1e88e5 100%)' } }}>Add Milestone</Button>
                </Box>
                <Box sx={{ position: 'relative' }}>
                  {quoteMilestones && quoteMilestones.length > 0 && <Box sx={{ position: 'absolute', left: 28, top: 20, bottom: 20, width: 3, bgcolor: '#e2e8f0', zIndex: 0, display: { xs: 'none', md: 'block' }, borderRadius: 2 }} />}
                  <Grid container spacing={3}>
                    {quoteMilestones && quoteMilestones.map((milestone) => (
                      <Grid item xs={12} key={milestone.id}>
                        <Card elevation={0} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, p: 2.5, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: 'white', borderLeft: milestone.status === 'Completed' ? '6px solid #10b981' : '6px solid #3b82f6', ml: { md: 6 }, position: 'relative', overflow: 'visible', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(0,0,0,0.06)', borderColor: '#93c5fd' } }}>
                          <Box sx={{ position: 'absolute', left: -34 - 7, top: '50%', transform: 'translateY(-50%)', width: 20, height: 20, borderRadius: '50%', bgcolor: milestone.status === 'Completed' ? '#10b981' : '#3b82f6', border: '4px solid white', boxShadow: '0 0 0 4px #f8fafc, 0 4px 6px rgba(0,0,0,0.1)', zIndex: 1, display: { xs: 'none', md: 'block' }, transition: 'all 0.3s' }} />
                          <Box sx={{ flexGrow: 1, ml: { xs: 0, md: 1 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                              <Typography variant="h6" fontWeight="800" color="#1e293b">{milestone.name}</Typography>
                              <Chip label={milestone.status === 'Completed' ? 'Completed' : 'In Progress'} size="small" icon={milestone.status === 'Completed' ? <CheckCircleIcon style={{ color: '#059669' }} /> : <AccessTimeIcon style={{ color: '#2563eb' }} />} sx={{ fontWeight: 700, bgcolor: milestone.status === 'Completed' ? '#d1fae5' : '#dbeafe', color: milestone.status === 'Completed' ? '#065f46' : '#1e40af', height: 26, '& .MuiChip-icon': { color: milestone.status === 'Completed' ? '#059669' : '#2563eb' } }} />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#64748b', bgcolor: "#f8fafc", px: 1.5, py: 0.5, borderRadius: 2 }}>
                                <CalendarTodayIcon sx={{ fontSize: 16 }} />
                                <Typography variant="caption" fontWeight="700" letterSpacing={0.5}>DUE: {milestone.due_date ? new Date(milestone.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'No deadline'}</Typography>
                              </Box>
                              {milestone.status !== 'Completed' && <Box sx={{ width: 180, flexGrow: { xs: 1, md: 0 } }}><Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" color="#64748b" fontWeight="600">Timeline Progress</Typography></Box><LinearProgress variant="determinate" value={50} sx={{ height: 8, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { borderRadius: 4, background: 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)' } }} /></Box>}
                            </Box>
                          </Box>
                          <Divider orientation="vertical" flexItem sx={{ mx: 3, display: { xs: 'none', md: 'block' }, borderColor: '#f1f5f9' }} />
                          <Divider sx={{ my: 2, display: { xs: 'block', md: 'none' }, borderColor: '#f1f5f9' }} />
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                  {(!quoteMilestones || quoteMilestones.length === 0) && (
                    <Paper elevation={0} sx={{ p: 8, textAlign: 'center', bgcolor: "#f8fafc", borderRadius: 4, border: '2px dashed #cbd5e1', mt: 2 }}>
                      <FlagIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                      <Typography variant="h6" color="#334155" fontWeight="700" gutterBottom>No milestones set</Typography>
                      <Typography variant="body2" color="#64748b" sx={{ mb: 3 }}>Define project checkpoints and tracking dates for this quote.</Typography>
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setOpenMilestoneDialog(true)} disabled={isQuoteClosed} sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, borderWidth: 2, '&:hover': { borderWidth: 2 } }}>Add First Milestone</Button>
                    </Paper>
                  )}
                </Box>
              </Box>
            </TabPanel>

            {/* Tab 7: AI Analyze */}
            <TabPanel value={tabValue} index={7}>
              <Box sx={{ mt: 1, p: 2 }}>
             

                {/* Loading State */}
                {aiAnalyzeLoading && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 3 }}>
                    <CircularProgress size={48} sx={{ color: '#3b82f6' }} />
                    <Typography color="#64748b" fontWeight={600}>Running AI analysis on this quote...</Typography>
                    <LinearProgress sx={{ width: 300, maxWidth: '80%', borderRadius: 2, height: 6, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#3b82f6' } }} />
                  </Box>
                )}

                {/* Error State */}
                {aiAnalyzeError && !aiAnalyzeLoading && (
                  <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #fecaca', bgcolor: '#fef2f2', p: 4, textAlign: 'center' }}>
                    <WarningAmberIcon sx={{ fontSize: 48, color: '#ef4444', mb: 2 }} />
                    <Typography fontWeight="700" color="#b91c1c" mb={2}>Analysis Error</Typography>
                    <Typography variant="body2" color="#ef4444" mb={3}>{aiAnalyzeError}</Typography>
                    <Button variant="outlined" color="error" onClick={fetchAiAnalyze} sx={{ borderRadius: 2, fontWeight: 'bold' }}>
                      Retry
                    </Button>
                  </Card>
                )}

                {/* Empty State — No data yet, not loading (should not appear since it auto-loads) */}
                {!aiAnalyzeLoading && !aiAnalyzeError && !aiAnalyzeData && (
                  <Paper elevation={0} sx={{ p: 8, textAlign: 'center', bgcolor: "#f8fafc", borderRadius: 4, border: '2px dashed #cbd5e1' }}>
                    <PsychologyIcon sx={{ fontSize: 64, color: '#93c5fd', mb: 2 }} />
                    <Typography variant="h6" color="#334155" fontWeight="700" gutterBottom>
                      AI-Powered Quote Analysis
                    </Typography>
                    <Typography variant="body2" color="#64748b" sx={{ mb: 3 }}>
                      Analysis will load automatically...
                    </Typography>
                  </Paper>
                )}

                {/* Results State */}
                {aiAnalyzeData && !aiAnalyzeLoading && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Win Probability Card */}
                    {winPrediction && winPrediction.win_probability != null && (
                      <Card elevation={0} sx={{
                        borderRadius: 4, border: '1px solid #bfdbfe', bgcolor: 'white', overflow: 'hidden',
                        boxShadow: '0 4px 20px rgba(59,130,246,0.08)'
                      }}>
                        <Box sx={{
                          p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
                          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: '#3b82f6', color: 'white', width: 56, height: 56, boxShadow: '0 4px 14px rgba(59,130,246,0.3)' }}>
                              <PsychologyIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box>
                              <Typography variant="h4" fontWeight="900" color="#1e40af">
                                {winPrediction.win_probability}%
                              </Typography>
                              <Typography variant="caption" fontWeight="700" color="#3b82f6" textTransform="uppercase" letterSpacing={0.5}>
                                AI Win Probability
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ flex: 1, maxWidth: 300 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" color="#1e40af" fontWeight="600">Confidence Level</Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={winPrediction.win_probability}
                              sx={{
                                height: 10, borderRadius: 5, bgcolor: '#bfdbfe',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: winPrediction.win_probability >= 50 ? '#10b981' : '#f59e0b',
                                  borderRadius: 5
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      </Card>
                    )}

                    {/* Explanation (from analyze_quotation) */}
                    {aiAnalyzeData.explanation && (
                      <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #fef3c7', bgcolor: '#fffbeb', overflow: 'hidden', boxShadow: '0 4px 20px rgba(245,158,11,0.06)' }}>
                        <Box sx={{ p: 2.5, background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <PsychologyIcon sx={{ color: '#f59e0b', fontSize: 22 }} />
                          <Typography variant="subtitle2" fontWeight="800" color="#b45309" textTransform="uppercase" letterSpacing={0.5}>
                            AI Explanation
                          </Typography>
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                          <Typography variant="body2" sx={{ color: '#78350f', lineHeight: 1.7, fontWeight: 500 }}>
                            {aiAnalyzeData.explanation}
                          </Typography>
                        </CardContent>
                      </Card>
                    )}

                    {/* Primary Negotiation Leverage */}
                    <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #d1fae5', bgcolor: '#f0fdf4', overflow: 'hidden', boxShadow: '0 4px 20px rgba(16,185,129,0.06)' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#10b981', color: 'white', width: 40, height: 40 }}>
                            <TrendingUpIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="800" color="#065f46" textTransform="uppercase" letterSpacing={0.5} mb={0.5}>
                              Primary Negotiation Leverage
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#064e3b', lineHeight: 1.6 }}>
                              {aiAnalyzeData.ai_summary?.primary_negotiation_leverage || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Price Elasticity Insight */}
                    <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #dbeafe', bgcolor: '#eff6ff', overflow: 'hidden', boxShadow: '0 4px 20px rgba(59,130,246,0.06)' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#3b82f6', color: 'white', width: 40, height: 40 }}>
                            <AttachMoneyIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="800" color="#1e40af" textTransform="uppercase" letterSpacing={0.5} mb={0.5}>
                              Price Elasticity Insight
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1e3a5f', lineHeight: 1.6 }}>
                              {aiAnalyzeData.ai_summary?.price_elasticity_insight || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Rationale */}
                    <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', bgcolor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                          <Avatar sx={{ bgcolor: '#3b82f6', color: 'white', width: 40, height: 40 }}>
                            <InfoIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="800" color="#1e40af" textTransform="uppercase" letterSpacing={0.5} mb={0.5}>
                              Analysis Rationale
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.6 }}>
                              {aiAnalyzeData.ai_summary?.rationale || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Key Drivers & Risk Flags side by side */}
                    <Grid container spacing={3}>
                      {/* Key Drivers */}
                      <Grid item xs={12} md={6}>
                        <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #d1fae5', bgcolor: '#f0fdf4', height: '100%', boxShadow: '0 4px 20px rgba(16,185,129,0.06)' }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                              <CheckCircleIcon sx={{ color: '#10b981', fontSize: 24 }} />
                              <Typography variant="subtitle2" fontWeight="800" color="#065f46" textTransform="uppercase" letterSpacing={0.5}>
                                Key Drivers
                              </Typography>
                            </Box>
                            {aiAnalyzeData.ai_summary?.key_drivers && aiAnalyzeData.ai_summary.key_drivers.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {aiAnalyzeData.ai_summary.key_drivers.map((driver, idx) => (
                                  <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, bgcolor: 'white', borderRadius: 2, border: '1px solid #d1fae5' }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981', mt: 0.75, flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ color: '#064e3b', lineHeight: 1.5, fontSize: '0.85rem' }}>
                                      {driver}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="#94a3b8">No key drivers identified.</Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Risk Flags */}
                      <Grid item xs={12} md={6}>
                        <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #fecaca', bgcolor: '#fef2f2', height: '100%', boxShadow: '0 4px 20px rgba(239,68,68,0.06)' }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                              <WarningAmberIcon sx={{ color: '#ef4444', fontSize: 24 }} />
                              <Typography variant="subtitle2" fontWeight="800" color="#b91c1c" textTransform="uppercase" letterSpacing={0.5}>
                                Risk Flags
                              </Typography>
                            </Box>
                            {aiAnalyzeData.ai_summary?.risk_flags && aiAnalyzeData.ai_summary.risk_flags.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {aiAnalyzeData.ai_summary.risk_flags.map((risk, idx) => (
                                  <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, bgcolor: 'white', borderRadius: 2, border: '1px solid #fecaca' }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444', mt: 0.75, flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ color: '#7f1d1d', lineHeight: 1.5, fontSize: '0.85rem' }}>
                                      {risk}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="#94a3b8">No risk flags identified.</Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Recommendations */}
                    <Card elevation={0} sx={{
                      borderRadius: 4, border: '1px solid #bfdbfe', bgcolor: 'white', overflow: 'hidden',
                      boxShadow: '0 4px 20px rgba(59,130,246,0.08)'
                    }}>
                      <Box sx={{
                        p: 2.5, background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                        display: 'flex', alignItems: 'center', gap: 1.5
                      }}>
                        <PsychologyIcon sx={{ color: '#3b82f6', fontSize: 24 }} />
                        <Typography variant="subtitle2" fontWeight="800" color="#1e40af" textTransform="uppercase" letterSpacing={0.5}>
                          AI Recommendations
                        </Typography>
                      </Box>
                      <CardContent sx={{ p: 3 }}>
                        {aiAnalyzeData.ai_summary?.recommendations && aiAnalyzeData.ai_summary.recommendations.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {aiAnalyzeData.ai_summary.recommendations.map((rec, idx) => (
                              <Box key={idx} sx={{
                                display: 'flex', alignItems: 'flex-start', gap: 1.5,
                                p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0'
                              }}>
                                <Box sx={{
                                  width: 24, height: 24, borderRadius: '50%', bgcolor: '#3b82f6', color: 'white',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontWeight: 800, fontSize: '0.75rem', flexShrink: 0, mt: 0.25
                                }}>
                                  {idx + 1}
                                </Box>
                                <Typography variant="body2" sx={{ color: '#1e293b', lineHeight: 1.6, fontWeight: 600, fontSize: '0.85rem' }}>
                                  {rec}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="#94a3b8">No recommendations available.</Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </Box>
            </TabPanel>

          </Paper>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid #f0f0f0', mb: 2 }}>Assign Team Member</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1, minHeight: 200 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>Select a user to assign to this quote. They will receive notifications and updates.</Typography>
          <Autocomplete fullWidth options={users} getOptionLabel={(option) => option ? (option.full_name || option.username || '') : ''} value={users.find(u => String(u.id) === String(newAssignment)) || null} onChange={(event, newValue) => { setNewAssignment(newValue ? newValue.id : ''); }} renderInput={(params) => (<TextField {...params} label="Search User" variant="outlined" />)} />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setOpenAssignDialog(false)} sx={{ color: '#64748b', fontWeight: 'bold' }}>Cancel</Button>
          <Button onClick={handleAssignUser} variant="contained" disabled={assignLoading || !newAssignment} sx={{ borderRadius: 2, fontWeight: 'bold', px: 3 }}>{assignLoading ? 'Assigning...' : 'Assign User'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openMilestoneDialog} onClose={() => setOpenMilestoneDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid #f0f0f0', mb: 2 }}>Add Milestone</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField autoFocus label="Milestone Name" fullWidth variant="outlined" value={newMilestone} onChange={(e) => setNewMilestone(e.target.value)} />
          <TextField label="Due Date" type="date" fullWidth variant="outlined" InputLabelProps={{ shrink: true }} value={newMilestoneDate} onChange={(e) => setNewMilestoneDate(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setOpenMilestoneDialog(false)} sx={{ color: '#64748b', fontWeight: 'bold' }}>Cancel</Button>
          <Button onClick={handleAddMilestone} variant="contained" disabled={!newMilestone} sx={{ borderRadius: 2, fontWeight: 'bold', px: 3 }}>Add Milestone</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openFollowupDialog} onClose={() => setOpenFollowupDialog(false)} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 700, overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', p: 3, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
          <EventNoteIcon sx={{ fontSize: 32, opacity: 0.9 }} />
          <Box><Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>{editingFollowupId ? 'Edit Follow-up' : 'Log a Follow-up'}</Typography><Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>{editingFollowupId ? "Update details for this scheduled interaction." : "Record a new interaction or log an upcoming event."}</Typography></Box>
        </Box>
        <DialogContent sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: 'white' }}>
            <Typography variant="subtitle2" color="#64748b" fontWeight="800" textTransform="uppercase" mb={3}>Interaction Details</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}><TextField fullWidth label="Title / Short Note *" value={newFollowup} onChange={(e) => setNewFollowup(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
              <Grid item xs={12}><Autocomplete fullWidth options={users} getOptionLabel={(option) => option ? (option.full_name || option.username || '') : ''} value={users.find(u => String(u.id) === String(newFollowupAssignee)) || null} onChange={(e, val) => setNewFollowupAssignee(val ? val.id : '')} renderInput={(params) => <TextField {...params} label="Assign To / Visibility" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Detailed Description (Optional)" multiline rows={3} value={newFollowupDesc} onChange={(e) => setNewFollowupDesc(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
            </Grid>
          </Paper>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}><DateRangeIcon sx={{ color: '#3b82f6' }} /><Typography variant="subtitle2" color="#1e293b" fontWeight="800" textTransform="uppercase">Current Interaction Timing</Typography></Box>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}><TextField fullWidth type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={newFollowupStartDate} onChange={(e) => setNewFollowupStartDate(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth type="time" label="Start Time" InputLabelProps={{ shrink: true }} value={newFollowupStartTime} onChange={(e) => setNewFollowupStartTime(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth type="date" label="End Date" InputLabelProps={{ shrink: true }} value={newFollowupEndDate} onChange={(e) => setNewFollowupEndDate(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth type="time" label="End Time" InputLabelProps={{ shrink: true }} value={newFollowupEndTime} onChange={(e) => setNewFollowupEndTime(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
            </Grid>
          </Paper>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #ede9fe', bgcolor: '#f5f3ff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}><AccessTimeIcon sx={{ color: '#7c3aed' }} /><Typography variant="subtitle2" color="#5b21b6" fontWeight="800" textTransform="uppercase">Plan Next Visit</Typography></Box>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}><TextField fullWidth type="date" label="Next Visit Start Date" InputLabelProps={{ shrink: true }} value={newFollowupNextVisitStartDate} onChange={(e) => setNewFollowupNextVisitStartDate(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }} /></Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth type="time" label="Next Visit Start Time" InputLabelProps={{ shrink: true }} value={newFollowupNextVisitStartTime} onChange={(e) => setNewFollowupNextVisitStartTime(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }} /></Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth type="date" label="Next Visit End Date" InputLabelProps={{ shrink: true }} value={newFollowupNextVisitEndDate} onChange={(e) => setNewFollowupNextVisitEndDate(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }} /></Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth type="time" label="Next Visit End Time" InputLabelProps={{ shrink: true }} value={newFollowupNextVisitEndTime} onChange={(e) => setNewFollowupNextVisitEndTime(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }} /></Grid>
            </Grid>
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#6d28d9', fontStyle: 'italic', fontWeight: 500 }}>* Automatically creates a dedicated follow-up for the next visit dates provided.</Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 4, py: 3, borderTop: '1px solid #f1f5f9', bgcolor: 'white' }}>
          <Button onClick={() => setOpenFollowupDialog(false)} sx={{ borderRadius: 2, fontWeight: 'bold', color: '#64748b', px: 3 }}>Cancel</Button>
          <Button onClick={handleAddFollowup} variant="contained" disabled={isQuoteClosed || !newFollowup} sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, py: 1, boxShadow: 'none', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', '&:hover': { boxShadow: '0 4px 12px rgba(25,118,210,0.3)' } }}>{editingFollowupId ? 'Update' : 'Save Follow-up'}</Button>
        </DialogActions>
      </Dialog>

      {/* Add Product Dialog */}
      <Dialog open={openAddProductDialog} onClose={() => setOpenAddProductDialog(false)} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 880, overflow: 'hidden', boxShadow: '0 24px 48px rgba(0,0,0,0.16)' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', p: 3, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}><ShoppingCartIcon /></Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>Add Products</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>Select products and configure quantities, then click "Add to Quote".</Typography>
          </Box>
        </Box>
        <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8fafc' }}>
          {productError && <Alert severity="error" onClose={() => setProductError('')} sx={{ mb: 2 }}>{productError}</Alert>}
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: 'white', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b', textTransform: 'uppercase', letterSpacing: 0.5 }}>1. Select Product</Typography>
                <Autocomplete
                  fullWidth
                  options={allProducts}
                  getOptionLabel={(option) => option ? `${option.name || ''}${option.product_number ? ' — ' + option.product_number : ''}` : ''}
                  onChange={handleSelectProduct}
                  value={selectedProduct}
                  loading={loadingProducts}
                  renderInput={(params) => <TextField {...params} label="Product" placeholder="Search products…" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />}
                />
                <TextField fullWidth type="number" label="Qty" value={productForm.quantity} onChange={(e) => handleProductFormChange('quantity', e.target.value)} inputProps={{ min: 0, step: 'any' }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                <TextField fullWidth type="number" label="Price/Unit" value={productForm.price_per_unit} onChange={(e) => handleProductFormChange('price_per_unit', e.target.value)} inputProps={{ min: 0, step: 'any' }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                <FormControl fullWidth>
                  <InputLabel id="add-product-unit-label">Unit</InputLabel>
                  <Select
                    labelId="add-product-unit-label"
                    label="Unit"
                    value={productForm.uom_id || ''}
                    onChange={(e) => handleProductFormChange('uom_id', e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    {uomOptions.map((u) => (
                      <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField fullWidth label="Description" multiline rows={2} value={productForm.description} onChange={(e) => handleProductFormChange('description', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                <Button variant="contained" fullWidth startIcon={<AddIcon />} onClick={handleAddCurrentItem} disabled={!selectedProduct} sx={{ borderRadius: 2, fontWeight: 'bold', py: 1.2, boxShadow: 'none', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', '&:hover': { boxShadow: '0 4px 12px rgba(25,118,210,0.3)' } }}>Add Item</Button>
              </Paper>
            </Grid>

            {/* Right: editable pending list */}
            <Grid item xs={12} md={7}>
              <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: 'white', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b', textTransform: 'uppercase', letterSpacing: 0.5 }}>2. Review Items</Typography>
                  <Chip label={`${pendingProducts.length} item${pendingProducts.length !== 1 ? 's' : ''}`} size="small" sx={{ fontWeight: 700, bgcolor: '#e0e7ff', color: '#4f46e5' }} />
                </Box>
                {pendingProducts.length === 0 ? (
                  <Box sx={{ p: 5, textAlign: 'center', color: '#94a3b8' }}>
                    <ShoppingCartIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                    <Typography variant="body2">No items added yet. Search and add a product on the left.</Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {pendingProducts.map((p, idx) => (
                      <Paper key={p.key} elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#fafbfd' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#e0e7ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800 }}>{idx + 1}</Typography>
                          </Box>
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 800, color: '#1e293b' }}>{p.name || '—'}</Typography>
                              <IconButton size="small" color="error" onClick={() => handleRemovePendingItem(p.key)} sx={{ mt: -0.5, mr: -0.5 }}><DeleteIcon fontSize="small" /></IconButton>
                            </Box>
                            <TextField fullWidth size="small" label="Description" value={p.description || ''} onChange={(e) => handleUpdatePendingItem(p.key, 'description', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }} />
                            <Grid container spacing={1.5}>
                              <Grid item xs={4}>
                                <TextField fullWidth size="small" type="number" label="Qty" value={p.quantity || ''} onChange={(e) => handleUpdatePendingItem(p.key, 'quantity', e.target.value)} inputProps={{ min: 0, step: 'any' }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }} />
                              </Grid>
                              <Grid item xs={4}>
                                <FormControl fullWidth size="small">
                                  <InputLabel id={`pending-unit-label-${p.key}`}>Unit</InputLabel>
                                  <Select
                                    labelId={`pending-unit-label-${p.key}`}
                                    label="Unit"
                                    value={p.uom_id || ''}
                                    onChange={(e) => handleUpdatePendingItem(p.key, 'uom_id', e.target.value)}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
                                  >
                                    {uomOptions.map((u) => (
                                      <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={4}>
                                <TextField fullWidth size="small" type="number" label="Price/Unit" value={p.price_per_unit || ''} onChange={(e) => handleUpdatePendingItem(p.key, 'price_per_unit', e.target.value)} inputProps={{ min: 0, step: 'any' }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }} />
                              </Grid>
                            </Grid>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 4, py: 3, borderTop: '1px solid #f1f5f9', bgcolor: 'white' }}>
          <Button onClick={() => setOpenAddProductDialog(false)} sx={{ borderRadius: 2, fontWeight: 'bold', color: '#64748b', px: 3 }}>Cancel</Button>
          <Button onClick={handleAddProductSubmit} variant="contained" disabled={pendingProducts.length === 0 || savingProduct} sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, py: 1, boxShadow: 'none', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', '&:hover': { boxShadow: '0 4px 12px rgba(25,118,210,0.3)' } }}>
            {savingProduct ? <CircularProgress size={24} color="inherit" /> : `Add to Quote (${pendingProducts.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Product Confirmation Dialog */}
      <Dialog open={Boolean(deleteProductTarget)} onClose={() => setDeleteProductTarget(null)} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 440, overflow: 'hidden', boxShadow: '0 24px 48px rgba(0,0,0,0.16)' } }}>
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#fee2e2', color: '#dc2626', width: 48, height: 48 }}><DeleteIcon /></Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Delete Product</Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
              Are you sure you want to remove{deleteProductTarget?.product_name || deleteProductTarget?.product_description ? ` "${deleteProductTarget.product_name || deleteProductTarget.product_description}"` : ' this product'} from the quote? This action cannot be undone.
            </Typography>
          </Box>
        </Box>
        {productError && <Alert severity="error" onClose={() => setProductError('')} sx={{ mx: 3, mb: 1 }}>{productError}</Alert>}
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid #f1f5f9', bgcolor: 'white' }}>
          <Button onClick={() => setDeleteProductTarget(null)} disabled={deletingProduct} sx={{ borderRadius: 2, fontWeight: 'bold', color: '#64748b', px: 3 }}>Cancel</Button>
          <Button onClick={confirmDeleteProduct} variant="contained" color="error" disabled={deletingProduct} sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, boxShadow: 'none', bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c', boxShadow: '0 4px 12px rgba(220,38,38,0.3)' } }}>
            {deletingProduct ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create New Revision Confirmation Dialog */}
      <Dialog open={reviseDialogOpen} onClose={() => { if (!revising) setReviseDialogOpen(false); }} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 480, overflow: 'hidden', boxShadow: '0 24px 48px rgba(0,0,0,0.16)' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: 'white', width: 44, height: 44 }}><AutorenewIcon /></Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'white', lineHeight: 1.2 }}>Create New Revision</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>Quote {quote?.name || ''}</Typography>
          </Box>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, bgcolor: '#fffbeb', borderRadius: 2, border: '1px solid #fde68a', mb: 2 }}>
            <WarningAmberIcon sx={{ color: '#d97706', fontSize: 20, mt: 0.2 }} />
            <Typography variant="body2" sx={{ color: '#92400e', lineHeight: 1.5 }}>
              This will <strong>close the current quote</strong> (Closed / Revision) and create a <strong>new quote</strong> with the same information and line items, plus an incremented revision number.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[
              ['Copy quote header & line items', true],
              ['Increment revision number (Rev.N)', true],
              ['Close the old quote as "Revision"', true],
            ].map(([label]) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                <Typography variant="body2" sx={{ color: '#334155' }}>{label}</Typography>
              </Box>
            ))}
          </Box>
          {reviseError && <Alert severity="error" onClose={() => setReviseError('')} sx={{ mt: 2 }}>{reviseError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid #f1f5f9', bgcolor: 'white' }}>
          <Button onClick={() => setReviseDialogOpen(false)} disabled={revising} sx={{ borderRadius: 2, fontWeight: 'bold', color: '#64748b', px: 3 }}>Cancel</Button>
          <Button onClick={confirmRevise} variant="contained" disabled={revising} sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, py: 1, boxShadow: 'none', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', '&:hover': { boxShadow: '0 4px 12px rgba(217,119,6,0.35)', background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)' } }}>
            {revising ? <CircularProgress size={24} color="inherit" /> : 'Create Revision'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Project from Won Quote Dialog (same design as standard Create Project) */}
      <Dialog open={createProjectDialogOpen} onClose={() => { if (!creatingProjectFromQuote) setCreateProjectDialogOpen(false); }} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 600 } }}>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>{createProjectData.quote_ref ? 'Create Project from Quote' : 'Create New Project'}</DialogTitle>
        <DialogContent dividers sx={{ borderColor: '#f1f5f9', py: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Name" fullWidth required value={createProjectData.name} onChange={e => setCreateProjectData({ ...createProjectData, name: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <TextField label="Description" fullWidth multiline rows={3} value={createProjectData.description} onChange={e => setCreateProjectData({ ...createProjectData, description: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <TextField label="Quote Ref (or N/A)" fullWidth required value={createProjectData.quote_ref} onChange={e => setCreateProjectData({ ...createProjectData, quote_ref: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Start Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={createProjectData.start_date} onChange={e => setCreateProjectData({ ...createProjectData, start_date: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              <TextField label="Due Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={createProjectData.due_date} onChange={e => setCreateProjectData({ ...createProjectData, due_date: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>

            <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <InputLabel>Priority</InputLabel>
              <Select value={createProjectData.priority} label="Priority" onChange={e => setCreateProjectData({ ...createProjectData, priority: e.target.value })}>
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                <InputLabel>Country</InputLabel>
                <Select value={createProjectData.country_id} label="Country" onChange={e => setCreateProjectData({ ...createProjectData, country_id: e.target.value })}>
                  {countries.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                <InputLabel>Division</InputLabel>
                <Select value={createProjectData.division_id} label="Division" onChange={e => setCreateProjectData({ ...createProjectData, division_id: e.target.value })}>
                  {divisions.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          </Box>
          {createProjectError && <Alert severity="error" onClose={() => setCreateProjectError('')} sx={{ mt: 2 }}>{createProjectError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: '1px solid #f1f5f9' }}>
          <Button onClick={() => setCreateProjectDialogOpen(false)} disabled={creatingProjectFromQuote} sx={{ borderRadius: 2, fontWeight: 'bold', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={confirmCreateProjectFromQuote} disabled={creatingProjectFromQuote} sx={{ borderRadius: 2, fontWeight: 'bold', boxShadow: 'none', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', '&:hover': { boxShadow: '0 4px 12px rgba(25,118,210,0.3)' } }}>
            {creatingProjectFromQuote ? <CircularProgress size={24} color="inherit" /> : 'Create Project'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default QuoteDetails;